import React, { useEffect, useRef } from 'react';
import { Columns3 } from 'lucide-react';

export interface PaneContextMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

interface PaneContextMenuProps {
  /** Viewport-space screen coordinates where the context menu should anchor. */
  x: number;
  y: number;
  items: PaneContextMenuItem[];
  onClose: () => void;
}

/**
 * Minimal floating context menu shown on canvas right-click. Closes on any
 * outside click, Escape, or item selection.
 */
const PaneContextMenu: React.FC<PaneContextMenuProps> = ({ x, y, items, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Defer attachment so the same right-click that opened the menu doesn't
    // immediately close it.
    const t = window.setTimeout(() => {
      window.addEventListener('mousedown', onDoc);
      window.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="fixed z-50 min-w-[180px] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg py-1 text-sm"
      style={{ left: x, top: y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, i) => (
        <button
          key={i}
          onClick={() => { item.onClick(); onClose(); }}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          {item.icon ?? <Columns3 size={14} className="text-slate-400" />}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
};

export default PaneContextMenu;
